// FTEDN — Community store (Zustand)
import { create } from 'zustand';
import { apiClient } from '../hooks/useApi';

export type PostType = 'discussion' | 'question' | 'announcement';

export interface CommunityPost {
  id: string;
  author: string;
  authorLevel: number;
  content: string;
  type: PostType;
  likes: number;
  comments: number;
  xp: number;
  userLiked: boolean;
  createdAt: string;
}

interface CommunityState {
  posts: CommunityPost[];
  loading: boolean;
  error: string | null;
  fetchPosts: () => Promise<void>;
  likePost: (postId: string) => Promise<boolean>;
  createPost: (content: string, type?: PostType) => Promise<boolean>;
}

function resolveAuthorName(raw: Record<string, unknown>): string {
  const author = raw.author;
  if (typeof author === 'string' && author.trim()) return author;
  if (author && typeof author === 'object' && 'name' in author) {
    const name = (author as { name?: unknown }).name;
    if (typeof name === 'string' && name.trim()) return name;
  }
  if (typeof raw.authorName === 'string' && raw.authorName.trim()) return raw.authorName;
  if (typeof raw.displayName === 'string' && raw.displayName.trim()) return raw.displayName;
  return 'Anonymous';
}

/** Map raw Firestore community post doc */
export function mapPost(raw: Record<string, unknown>): CommunityPost {
  return {
    id: String(raw.id ?? ''),
    author: resolveAuthorName(raw),
    authorLevel: Number(raw.authorLevel ?? raw.level ?? 1),
    content: String(raw.content ?? raw.body ?? ''),
    type: (raw.type as PostType) ?? 'discussion',
    likes: Number(raw.likes ?? raw.likeCount ?? 0),
    comments: Number(raw.comments ?? raw.commentCount ?? 0),
    xp: Number(raw.xp ?? 0),
    userLiked: Boolean(raw.userLiked ?? false),
    createdAt: String(raw.createdAt ?? ''),
  };
}

export const useCommunityStore = create<CommunityState>((set, get) => ({
  posts: [],
  loading: false,
  error: null,

  fetchPosts: async () => {
    set({ loading: true, error: null });
    try {
      const res = await apiClient.get('/community/posts');
      if (res.success && Array.isArray(res.data)) {
        set({ posts: res.data.map(mapPost), loading: false });
      } else {
        set({ error: 'Failed to load posts', loading: false });
      }
    } catch (err) {
      set({ error: (err as Error).message, loading: false });
    }
  },

  likePost: async (postId) => {
    try {
      const res = await apiClient.post(`/community/posts/${postId}/like`);
      if (res.success) {
        set({
          posts: get().posts.map(p =>
            p.id === postId ? { ...p, likes: p.likes + 1, userLiked: true } : p
          ),
        });
        return true;
      }
      return false;
    } catch {
      return false;
    }
  },

  createPost: async (content, type = 'discussion') => {
    try {
      const res = await apiClient.post('/community/posts', { content, type });
      return res.success === true;
    } catch {
      return false;
    }
  },
}));
